import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, Paperclip, Trash2, Info, ChevronDown, Square } from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { VoiceNote } from "@/components/VoiceNote";
import { useSpeech } from "@/components/SpeechPlayer";
import { Markdown } from "@/components/Markdown";
import { sceneCue, motifFor } from "@/lib/sceneBus";
import { safeHref } from "@/lib/utils";

// Real web-search citations stored with the reply (research widget action) — shown under the bubble so they
// survive a refresh and stay visible when the scene card deck is not mounted. Only http(s) links are clickable.
const MsgSources = ({ actions }) => {
  const { tx } = useI18n();
  const items = (actions || []).filter((a) => a?.type === "widget" && a.widget?.kind === "research")
    .flatMap((a) => a.widget?.data?.items || []).slice(0, 6);
  if (!items.length) return null;
  return (
    <div className="mt-2 pt-2 border-t border-white/10 space-y-1" data-testid="chat-msg-sources">
      <div className="text-[0.62rem] uppercase tracking-wide text-white/40">{tx("Kaynaklar")}</div>
      {items.map((it, i) => {
        const href = safeHref(it.url);
        const label = <>{it.title || it.source}<span className="text-white/40">{it.source ? ` · ${it.source}` : ""}{it.published_label ? ` · ${it.published_label}` : ""}</span></>;
        return href
          ? <a key={i} href={href} target="_blank" rel="noopener noreferrer nofollow" data-testid="chat-msg-source" className="block text-[0.72rem] text-cyan-200/90 hover:text-cyan-100 truncate">{label}</a>
          : <div key={i} className="text-[0.72rem] text-white/60 truncate">{label}</div>;
      })}
    </div>
  );
};

/* Text chat + one-way voice note + spoken replies (Emergent credits). Real-time GPT-Live voice lives in
   LiveSession.jsx and is selected in Ayarlar → Live konuşma; it is never started from here. */
export const ChatVoice = ({ setOrbState, defaultModel = "auto", aiProvider, onDataChanged, onActions, compact = false, roomy = false, onExpand }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { t, tx, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [model, setModel] = useState(defaultModel);
  const [models, setModels] = useState(null);   // allowlist from the server; never invented here
  useEffect(() => {
    api.get("/ai/models").then((r) => setModels(r.data)).catch(() => setModels({ models: [] }));
  }, []);
  // a model that the active credential mode does not offer must not stay selected silently
  useEffect(() => {
    if (!models?.models) return;
    if (model !== "auto" && !models.models.some((m) => m.id === model)) setModel("auto");
  }, [models, model]);
  const [sending, setSending] = useState(false);
  const [image, setImage] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // auto-grow: the textarea follows the content up to the max height set in the style
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text, roomy]);
  const speech = useSpeech({ setOrbState, fallbackState: "idle" });
  useEffect(() => {
    const stop = () => speech.stop();
    const tst = (e) => toast.info(e.detail);
    window.addEventListener("jarvis-stop-speech", stop); window.addEventListener("jarvis-toast", tst);
    return () => { window.removeEventListener("jarvis-stop-speech", stop); window.removeEventListener("jarvis-toast", tst); };
  }, [speech]);

  const loadThreads = useCallback(async () => {
    try {
      const r = await api.get("/chat/threads");
      setThreads(r.data);
      setActiveThread((cur) => cur && r.data.some((t) => t.thread_id === cur) ? cur : (r.data[0]?.thread_id || null));
    } catch (e) {
      // never swallow silently — a failed load here is exactly what hides the default thread
      console.error("chat/threads load failed", e?.response?.status, e?.message);
      toast.error(tx("Sohbet sekmeleri yüklenemedi, tekrar deneniyor…"));
      setTimeout(() => { api.get("/chat/threads").then((r) => { setThreads(r.data); setActiveThread((c) => c || r.data[0]?.thread_id || null); }).catch(() => {}); }, 1200);
    }
  }, [tx]);
  const loadHistory = useCallback(async (tid) => {
    try { const r = await api.get("/chat/history", { params: tid ? { thread_id: tid } : {} }); setMessages(r.data); }
    catch (e) { console.error("chat/history load failed", e?.response?.status, e?.message); }
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (activeThread) loadHistory(activeThread); }, [activeThread, loadHistory]);
  const newThread = async () => {
    try { const r = await api.post("/chat/threads", { title: tx("Yeni sohbet") }); await loadThreads(); setActiveThread(r.data.thread_id); setMessages([]); }
    catch { toast.error(tx("Sekme oluşturulamadı")); }
  };
  const renameThread = async (tid) => {
    const cur = threads.find((t) => t.thread_id === tid);
    const title = window.prompt(tx("Sekme adı"), cur?.title || "");
    if (title == null) return;
    try { await api.put(`/chat/threads/${tid}`, { title }); loadThreads(); } catch { toast.error(tx("Yeniden adlandırılamadı")); }
  };
  const deleteThread = async (tid) => {
    if (!window.confirm(tx("Bu sohbet sekmesi ve geçmişi silinsin mi?"))) return;
    try { await api.delete(`/chat/threads/${tid}`); const rest = threads.filter((t) => t.thread_id !== tid); setThreads(rest); setActiveThread(rest[0]?.thread_id || null); if (!rest.length) loadThreads(); }
    catch { toast.error(tx("Silinemedi")); }
  };
  // per-thread model override (kept per browser); the stale-model guard above resets ids the source no longer offers
  useEffect(() => {
    const saved = activeThread && localStorage.getItem(`jarvis-thread-model-${activeThread}`);
    setModel(saved || "auto");
  }, [activeThread, defaultModel]);
  const pickModel = (m) => { setModel(m); if (activeThread) localStorage.setItem(`jarvis-thread-model-${activeThread}`, m); };
  const streamCtl = useRef(null);
  const STREAMING = aiProvider === "local" || aiProvider === "anthropic";

  // Streaming (AI PC / Claude): text only; Stop = real cancellation (the server closes the provider stream).
  const sendStream = async (body, tempId) => {
    const ctl = new AbortController();
    streamCtl.current = ctl;
    const liveId = `live_${Date.now()}`;
    setMessages((m) => [...m, { msg_id: liveId, role: "assistant", text: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST", credentials: "include", signal: ctl.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: body, model, language: lang, thread_id: activeThread }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `HTTP ${res.status}`); }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n"); buf = chunks.pop();
        for (const c of chunks) {
          if (!c.startsWith("data:")) continue;
          let ev; try { ev = JSON.parse(c.slice(5)); } catch { continue; }
          if (ev.type === "start") setMessages((m) => m.map((x) => (x.msg_id === tempId ? ev.user_message : x)));
          else if (ev.type === "delta") setMessages((m) => m.map((x) => (x.msg_id === liveId ? { ...x, text: x.text + ev.text } : x)));
          else if (ev.type === "error") setMessages((m) => m.map((x) => (x.msg_id === liveId ? { ...x, status: "error", text: `${x.text}\n⚠️ ${tx(ev.message)}`, fallback_offer: ev.fallback_offer } : x)));
          else if (ev.type === "done") setMessages((m) => m.map((x) => (x.msg_id === liveId ? { ...ev.assistant_message, fallback_offer: x.fallback_offer, status: x.status === "error" ? "error" : ev.assistant_message.status } : x)));
        }
      }
    } catch (e) {
      if (e.name === "AbortError") {
        setMessages((m) => m.map((x) => (x.msg_id === liveId ? { ...x, streaming: false, cancelled: true } : x)));
      } else {
        setMessages((m) => m.map((x) => (x.msg_id === liveId ? { ...x, streaming: false, status: "error", text: `⚠️ ${tx(e.message)}` } : x)));
      }
    } finally {
      streamCtl.current = null;
      setMessages((m) => m.map((x) => (x.msg_id === tempId ? { ...x, pending: false } : x)));
    }
  };
  const stopStream = () => streamCtl.current?.abort();

  // explicit, user-chosen retry of the same text on another configured source (never automatic)
  const retryWith = async (text, provider) => {
    if (sending) return;
    setSending(true); setOrbState("thinking");
    try {
      const r = await api.post("/chat/message", { text, model: "auto", language: lang, thread_id: activeThread, provider_override: provider });
      setMessages((m) => [...m, r.data.user_message, r.data.assistant_message]);
    } catch (e) { toast.error(tx(e?.response?.data?.detail || "Mesaj gönderilemedi — mesajınız geri yüklendi.")); }
    finally { setSending(false); setOrbState("idle"); }
  };
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages, sending]);

  /* The user's message and the typing state appear INSTANTLY (optimistic), so the UI never looks frozen
     while the model + tools (e.g. a real Gmail read) are still working. */
  const sendText = async () => {
    const body = text.trim();
    if ((!body && !image) || sending) return;        // also blocks a double click / double Enter
    const tempId = `tmp_${Date.now()}`;
    const img = image;
    setMessages((m) => [...m, { msg_id: tempId, role: "user", text: body, has_image: !!img, pending: true }]);
    setText(""); setImage(null); setSending(true);
    speech.stop();
    setOrbState("thinking");
    if (STREAMING && !img) {
      try { await sendStream(body, tempId); if (activeThread) loadThreads(); }
      finally { setSending(false); setOrbState("idle"); }
      return;
    }
    try {
      const r = await api.post("/chat/message", {
        text: body, image_base64: img, model, language: lang, thread_id: activeThread,
      });
      setMessages((m) => [...m.filter((x) => x.msg_id !== tempId), r.data.user_message, r.data.assistant_message]);
      if (onDataChanged) onDataChanged();
      if (activeThread) loadThreads();
      const a = r.data.assistant_message;
      if (a?.actions?.length && onActions) {
        // a real tool actually ran (the response carries its actions) — theme the backdrop to that work
        const scr = a.actions[a.actions.length - 1]?.screen || a.actions.find((x) => x.screen)?.screen || "";
        sceneCue("scan", motifFor(scr));
        onActions(a.actions);
        setTimeout(() => sceneCue(a.status === "error" ? "error" : "ok"), 420);
      }
    } catch (e) {
      const timeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      sceneCue("error");
      setMessages((m) => m.map((x) => (x.msg_id === tempId
        ? { ...x, pending: false, failed: true } : x)));
      setText((prev) => prev || body);               // nothing the user typed is lost
      toast.error(timeout ? tx("Yanıt zaman aşımına uğradı — mesajınız geri yüklendi, tekrar gönderin.")
        : (e?.response?.data?.detail || tx("Mesaj gönderilemedi — mesajınız geri yüklendi.")));
    } finally {
      setSending(false);
      setOrbState("idle");
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setImage(reader.result); toast.success(tx("{name} eklendi", { name: f.name })); };
    reader.readAsDataURL(f);
  };

  const clearChat = async () => { await api.delete("/chat/history", { params: activeThread ? { thread_id: activeThread } : {} }); setMessages([]); };

  return (
    <div className={`panel ${compact ? "p-3" : roomy ? "p-3 sm:p-4 h-full" : "corner-bracket p-4 h-full"} flex flex-col`}
      data-testid={compact ? "chat-compact" : "chat-voice-controls"}>
      {!compact && <div className={`flex items-center justify-between mb-2 ${roomy ? "pr-12" : ""}`}>
        <span className="label-eyebrow">{tx("Sohbet · Chat")}</span>
        <div className="flex items-center gap-2">
          <select value={model} onChange={(e) => pickModel(e.target.value)} data-testid="chat-model-select" aria-label={tx("Model")}
            title={models?.auto?.rule || ""}
            className="bg-black/30 border border-cyan-500/20 rounded-md px-2 py-1 text-[0.68rem] font-mono text-cyan-200 focus:outline-none">
            <option value="auto" className="bg-[#0C1222]">{tx("Otomatik")}</option>
            {(models?.models || []).map((m) => (
              <option key={m.id} value={m.id} className="bg-[#0C1222]">{m.label}</option>
            ))}
          </select>
          <button data-testid="clear-chat-btn" onClick={clearChat}
            className="text-xs text-[color:var(--txt-2)] hover:text-cyan-300 flex items-center gap-1">
            <Trash2 size={13} /> {t("clearChat")}
          </button>
        </div>
      </div>}

      {!compact && (
        <div className="flex items-center gap-1.5 mb-2 overflow-x-auto scrollbar-thin" data-testid="chat-threads">
          {threads.map((th) => (
            <button key={th.thread_id} onClick={() => setActiveThread(th.thread_id)}
              onDoubleClick={() => renameThread(th.thread_id)} data-testid={`chat-thread-${th.thread_id}`}
              title={tx("Tıkla: aç · çift tıkla: yeniden adlandır")}
              className={`group shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs ${activeThread === th.thread_id ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-white/60 hover:text-cyan-200"}`}>
              <span className="max-w-[120px] truncate">{th.title}</span>
              {threads.length > 1 && (
                <span onClick={(e) => { e.stopPropagation(); deleteThread(th.thread_id); }}
                  className="opacity-40 group-hover:opacity-100 hover:text-red-300" data-testid={`chat-thread-del-${th.thread_id}`}>×</span>
              )}
            </button>
          ))}
          <button onClick={newThread} data-testid="chat-thread-new"
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-cyan-400/30 text-cyan-300/80 hover:text-cyan-200 text-xs">
            + {tx("Sekme")}
          </button>
        </div>
      )}
      <div ref={scrollRef} className={`${compact ? "max-h-[110px]" : roomy ? "flex-1 min-h-0" : "flex-1 min-h-[180px]"} overflow-y-auto scrollbar-thin space-y-3 pr-1`} data-testid="chat-messages">
        {messages.length === 0 && !compact && (
          <div className="text-[color:var(--txt-3)] text-sm text-center mt-10">
            {t("ask")}
          </div>
        )}
        {(compact ? messages.slice(-1) : messages).map((m, i) => (
          <div key={m.msg_id || i} className={`msg-in flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "whitespace-pre-wrap" : ""} ${
              m.role === "user" ? "bg-blue-500/20 border border-blue-400/30 text-blue-50"
              : m.status === "api_required" || m.status === "error" || m.status === "unsupported_in_mode"
                ? "bg-amber-500/10 border border-amber-400/30 text-amber-100"
                : "bg-cyan-500/10 border border-cyan-400/20 text-cyan-50"
            }`} data-testid={`chat-msg-${m.role}`} data-pending={m.pending ? "1" : undefined}
              data-failed={m.failed ? "1" : undefined}>
              {m.has_image && <div className="text-xs opacity-70 mb-1">📎 {tx("görsel")}</div>}
              {m.role === "assistant" ? <><Markdown text={m.text} /><MsgSources actions={m.actions} /></> : m.text}
              {m.failed && <div className="text-[0.62rem] text-red-300 mt-1">{tx("gönderilemedi")}</div>}
              {m.cancelled && <div className="text-[0.62rem] text-white/45 mt-1">{tx("yanıt durduruldu")}</div>}
              {m.role === "assistant" && m.fallback_offer?.length > 0 && (() => {
                const prev = [...messages.slice(0, i)].reverse().find((x) => x.role === "user");
                return prev?.text ? (
                  <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="fallback-offer">
                    {m.fallback_offer.map((p) => (
                      <button key={p} onClick={() => retryWith(prev.text, p)} disabled={sending}
                        className="text-[0.66rem] px-2 py-1 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10">
                        {tx("Bu mesajı şununla tekrar gönder")}: {({ emergent: "Platform", openai: "OpenAI", anthropic: "Claude", local: "AI PC" })[p] || p}
                      </button>
                    ))}
                  </div>
                ) : null;
              })()}
              {/* the used model stays in the backend metadata (m.model) but is no longer shown in the UI */}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="rounded-2xl px-3.5 py-2 text-sm bg-cyan-500/10 border border-cyan-400/20 text-cyan-100/80 flex items-center gap-2">
              <span className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: "240ms" }} />
              </span>
              {tx("yanıt hazırlanıyor…")}
            </div>
          </div>
        )}
      </div>

      {image && (
        <div className="mt-2 text-xs text-cyan-300 flex items-center gap-2">
          <img src={image} alt="" className="h-10 w-10 object-cover rounded" />
          <button onClick={() => setImage(null)} className="hover:text-red-400">{tx("× kaldır")}</button>
        </div>
      )}

      {/* input row — in the roomy (Yazışma) layout the textarea shows 3–4 lines and grows while typing;
          the right padding keeps the send button clear of the corner orb. */}
      <div className={`mt-3 flex items-end gap-2 ${roomy ? "sm:pr-24" : ""}`}>
        <label className="p-2 mb-0.5 rounded-full hover:bg-cyan-500/15 cursor-pointer text-[color:var(--txt-2)] hover:text-cyan-300" data-testid="attach-btn" title={t("attach")}>
          <Paperclip size={18} />
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
        <textarea
          data-testid="chat-input"
          ref={inputRef}
          rows={roomy ? 4 : 1}
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
          placeholder={roomy ? `${t("ask")} — ${tx("Enter gönderir, Shift+Enter alt satır")}` : t("ask")}
          className={`flex-1 resize-none bg-black/30 border border-cyan-500/20 px-4 py-2.5 text-sm text-white placeholder:text-[color:var(--txt-3)] focus:outline-none focus:border-cyan-400/60 scrollbar-thin ${roomy ? "rounded-2xl leading-relaxed" : "rounded-3xl"}`}
          style={roomy ? { minHeight: "6.5rem", maxHeight: "32vh" } : { maxHeight: "6rem" }}
        />
        {sending && STREAMING ? (
          <button data-testid="stop-stream-btn" onClick={stopStream} aria-label={tx("Durdur")}
            className="p-2.5 mb-0.5 rounded-full bg-red-500/20 border border-red-400/40 text-red-200 hover:bg-red-500/35">
            <Square size={18} />
          </button>
        ) : (
          <button data-testid="send-btn" onClick={sendText} disabled={sending} aria-label={tx("Gönder")}
            className="p-2.5 mb-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/35 disabled:opacity-40">
            <Send size={18} />
          </button>
        )}
      </div>

      {/* voice note (one-way STT → text). The written chat is text-only: no read-aloud buttons and no
          TTS toggle here; real spoken conversation runs through the orb (Live / voice session). */}
      <div className={`mt-3 flex items-start justify-between gap-3 flex-wrap ${roomy ? "sm:pr-24" : ""}`}>
        <VoiceNote lang={lang} setOrbState={setOrbState} onTranscript={(txt) => setText((prev) => (prev ? `${prev} ${txt}` : txt))} />
      </div>
      {!compact && <button onClick={() => setShowInfo((v) => !v)} data-testid="voice-info-toggle" aria-expanded={showInfo}
        className="mt-1 inline-flex items-center gap-1 text-[0.62rem] text-white/40 hover:text-cyan-300">
        <Info size={11} /> {tx("Ses nasıl çalışır?")} <ChevronDown size={11} className={`transition-transform ${showInfo ? "rotate-180" : ""}`} />
      </button>}
      {showInfo && (
        <div className="mt-1 text-[0.64rem] text-white/55 bg-black/25 border border-white/10 rounded-lg px-3 py-2 msg-in" data-testid="voice-pipeline-note">
          {tx("Akış: ses → metin")} ({speech.status ? "whisper-1" : "STT"}) → Luna → {tx("ses")} ({speech.status?.model || "TTS"}, {tx("ses")}: {speech.status?.voice || "—"}). {tx("Emergent kredisi kullanılır")} (~${speech.status?.per_1m_chars_usd ?? 15}/1M {tx("karakter")}).
          {" "}{tx("Türkçe desteklenir; sesler İngilizce için optimize olduğundan hafif aksan olabilir. Araya girilebilen gerçek GPT-Live konuşma ayrı bir moddur: Ayarlar → Live konuşma. Ses ve model ayarları: Ayarlar.")}
        </div>
      )}

      {compact && (
        <button onClick={onExpand} data-testid="chat-expand-btn" className="mt-1 self-end text-[0.66rem] text-white/45 hover:text-cyan-300">{tx("Tüm sohbeti aç ›")}</button>
      )}
    </div>
  );
};

export default ChatVoice;
